import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AiOutlineClose, AiOutlineExclamationCircle } from "react-icons/ai";
import { FiDownload, FiUpload, FiRefreshCw, FiSave } from "react-icons/fi";
import download from "downloadjs";

import Axios from "../../../../_services/caller.service";
import Loader from "../../../UiComponents/loader/Loader";
import ModalConfirmNewPreset from "../modal/ModalConfirmNewPreset";
import SortablePreset from "../sortable/SortablePreset";
import styles from "./DefaultPresetConfig.module.scss";

/**
 * Editeur generique des presets PAR DEFAUT (referentiel central).
 * Gere aussi bien le type "test" que "cr" selon la prop `type`.
 *
 * Props :
 *  - type : "test" | "cr"
 *  - setOverlay : setter de l'overlay du parent
 */
const DefaultPresetEditor = ({ type, setOverlay }) => {
  const { t } = useTranslation();
  const isTest = type === "test";

  const getUrl = isTest ? "/defaultPresetTest" : "/defaultPresetCr";
  const putUrl = isTest ? "/defaultPresetTest" : "/defaultPresetCr";
  const requiredTag = isTest ? "<Preset>" : "<PDFPreset>";
  const exportName = isTest
    ? "Default-Presets-Test.txt"
    : "Default-Presets-CR.txt";
  const title = isTest ? t("preset.preset_test") : t("preset.preset_reports");

  const [presets, setPresets] = useState([]);
  const [displayDescription, setDisplayDescription] = useState(null);
  const [maj, setMaj] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState();
  const [modalConfirm, setModalConfirm] = useState(false);
  const [isNotGoodImportFileType, setIsNotGoodImportFileType] = useState(false);

  useEffect(() => {
    Axios.get(getUrl)
      .then((response) => {
        setPresets(response.data.data || []);
        setIsNotGoodImportFileType(false);
      })
      .catch(() => {
        toast.error(t("preset.load_error"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maj, type]);

  const exportPresets = () => {
    const dataStr = JSON.stringify(presets);
    download(dataStr, exportName, "text/plain");
  };

  useEffect(() => {
    if (!selectedFile) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (
          !Array.isArray(data) ||
          data.length === 0 ||
          !data[0]?.preset ||
          !data[0].preset.includes(requiredTag)
        ) {
          setIsNotGoodImportFileType(true);
          toast.error(t("preset.wrong_preset_type"));
          setSelectedFile(undefined);
          return;
        }
        setPresets((prev) => [...prev, ...data]);
        setIsNotGoodImportFileType(false);
      } catch (err) {
        setIsNotGoodImportFileType(true);
        toast.error(t("preset.wrong_preset_type"));
      }
      setSelectedFile(undefined);
    };
    reader.readAsText(selectedFile, "ISO-8859-1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  const activeModalConfirm = () => {
    setOverlay(true);
    setModalConfirm(true);
  };

  const registerDefaults = () => {
    setLoading(true);
    Axios.put(putUrl, { data: presets })
      .then(() => {
        setLoading(false);
        setSuccess(true);
        toast.success(t("preset.save_success"));
        setTimeout(() => {
          setSuccess(false);
          setModalConfirm(false);
          setOverlay(false);
        }, 1500);
      })
      .catch(() => {
        setLoading(false);
        toast.error(t("preset.save_error"));
      });
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{title}</h3>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.btnGhost}
          onClick={exportPresets}
        >
          <FiDownload /> {t("preset.export")}
        </button>

        <label className={styles.fileLabel}>
          <FiUpload /> {t("preset.import")}
          <input
            type="file"
            onChange={(e) => setSelectedFile(e.target.files[0])}
            hidden
          />
        </label>

        <button
          type="button"
          className={styles.btnGhost}
          onClick={() => setMaj(!maj)}
        >
          <FiRefreshCw /> {t("common.refresh")}
        </button>
      </div>

      {isNotGoodImportFileType && (
        <div className={styles.alertWarning}>
          <AiOutlineExclamationCircle />
          <span>{t("preset.wrong_preset_type")}</span>
          <AiOutlineClose
            onClick={() => setIsNotGoodImportFileType(false)}
            className={styles.alertClose}
          />
        </div>
      )}

      <SortablePreset
        presetType={presets}
        setPresetType={setPresets}
        displayDescription={displayDescription}
        setDisplayDescription={setDisplayDescription}
      />

      <div className={styles.saveBar}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={activeModalConfirm}
        >
          <FiSave /> {t("preset.save_defaults")}
        </button>
      </div>

      {modalConfirm && (
        <ModalConfirmNewPreset
          setOverlay={setOverlay}
          setModalConfirmRegisterNewPreset={setModalConfirm}
          registerNewValue={registerDefaults}
          success={success}
        />
      )}
      {loading && <Loader />}
    </div>
  );
};

const DefaultPresetConfig = ({ setOverlay }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.wrapper}>
      <p className={styles.intro}>{t("preset.config_intro")}</p>
      <div className={styles.grid}>
        <DefaultPresetEditor type="test" setOverlay={setOverlay} />
        <DefaultPresetEditor type="cr" setOverlay={setOverlay} />
      </div>
    </div>
  );
};

export default DefaultPresetConfig;
